/**
 * ConfirmacionDestructiva — modal SSOT para acciones destructivas/peligrosas.
 *
 * POR QUE EXISTE: el `confirm()` nativo del navegador es gris, no explica las
 * consecuencias y se acepta con un Enter distraído. En un sistema con dinero
 * (desactivar un nodo operador que tiene cajas/cajeros/saldos colgando) eso es
 * un accidente esperando a pasar — de hecho ocurrió: el toggle de nodo
 * desactivaba aunque el usuario cancelara.
 *
 * QUE HACE: muestra un modal ROJO (peligro inequívoco) con:
 *   - título + ícono ⚠ con pulso sutil (no parpadeo molesto: lee como serio),
 *   - lista de consecuencias concretas ("sus POS entran en pausa", etc.),
 *   - TYPE-TO-CONFIRM: el usuario debe escribir el nombre exacto del recurso
 *     para habilitar el botón de acción. Imposible por accidente.
 *
 * API (Promise-based, reemplaza a confirm()):
 *
 *   const r = await ConfirmacionDestructiva.pedir({
 *       titulo: 'Desactivar nodo',
 *       textoConfirmacion: 'Cesar - OP#05',   // lo que el usuario debe teclear
 *       consecuencias: [
 *           'Sus POS entrarán en pausa visible',
 *           'Quedará oculto del árbol (botón "Inactivos" para verlo)',
 *       ],
 *       nota: 'Es reversible: podés reactivarlo cuando quieras.',
 *       etiquetaAccion: 'Desactivar',          // texto del botón rojo
 *       opcion: { texto: 'Incluir también toda su rama (descendientes)' }, // checkbox opcional
 *   });
 *   if (!r.confirmado) return;        // el usuario canceló
 *   const cascada = r.opcionMarcada;  // estado del checkbox (false si no hay opción)
 *
 * Resuelve `{ confirmado, opcionMarcada }`:
 *   - confirmado=true solo si pasó el gate (type-to-confirm si aplica) y aceptó.
 *   - confirmado=false si cancela, cierra con la X o Escape.
 *
 * MODOS:
 *   - Con `textoConfirmacion` no vacío → exige teclearlo (acción destructiva).
 *   - Sin `textoConfirmacion` → confirmación simple sin fricción (acción reversible).
 *
 * SSOT: reemplaza los confirm() del admin. Para destructivo usar textoConfirmacion;
 * para confirmaciones normales, omitirlo.
 */

import { App } from '../admin.js';
import { escapeHtmlParaInyeccionSegura } from '../utils/escape-html-para-inyeccion-segura.js';
import { t } from '../services/i18n-admin.js';
import { icon } from '../utils/icon.js';

const MODAL_ID = 'modalConfirmacionDestructiva';

// Una sola instancia viva a la vez. Guardamos el resolver de la Promise en curso
// para que cualquier vía de cierre (botón, X, overlay, Escape) la complete una
// única vez con el veredicto correcto.
let resolverPromesaEnCurso = null;
let textoEsperadoNormalizado = null;
let listenerTecladoActivo = null;

/** Normaliza para comparar el typed contra el esperado: trim + colapsa espacios. */
function normalizarTextoConfirmacion(texto) {
    return String(texto == null ? '' : texto)
        .trim()
        .replace(/\s+/g, ' ');
}

/**
 * Resuelve la promesa en curso UNA sola vez y limpia el estado global.
 * Idempotente: si ya se resolvió, no hace nada (evita doble-resolve por
 * eventos que se solapan, p.ej. click en botón + Escape casi simultáneos).
 * @param {boolean} confirmado
 */
function finalizar(confirmado) {
    const resolver = resolverPromesaEnCurso;
    // Leer el checkbox ANTES de cerrar el modal (después el estado se pierde).
    const checkEl = document.getElementById('confirmDestructivaOpcionCheck');
    const opcionMarcada = !!(confirmado && checkEl && checkEl.checked);
    // Leer el valor del selector extra (ej. nodo destino de reasignación) si lo hay.
    const selectEl = document.getElementById('confirmDestructivaSelect');
    const valorSeleccionado = confirmado && selectEl ? selectEl.value : null;
    resolverPromesaEnCurso = null;
    textoEsperadoNormalizado = null;
    if (listenerTecladoActivo) {
        document.removeEventListener('keydown', listenerTecladoActivo, true);
        listenerTecladoActivo = null;
    }
    App.closeModal();
    if (resolver) resolver({ confirmado, opcionMarcada, valorSeleccionado });
}

export const ConfirmacionDestructiva = {
    MODAL_ID,

    /**
     * Plantilla del modal — se inyecta una vez junto al resto en modals.js.
     * El contenido dinámico (título, consecuencias, texto a teclear) se hidrata
     * en pedir(); aquí solo va el esqueleto.
     */
    template() {
        return `
    <div class="modal" id="${MODAL_ID}" style="display:none; max-width:480px;">
      <div class="modal-header" id="confirmDestructivaHeader">
        <h2 class="modal-titulo-destructivo">
          ${icon('alert-triangle', 'icon-alerta-pulsante', { id: 'confirmDestructivaIcono' })}
          <span id="confirmDestructivaTitulo">${t('Confirmar acción')}</span>
        </h2>
        <button class="btn-icon" data-action="confirmDestructiva:cancelar" aria-label="${t('Cancelar')}">
          ${icon('x')}
        </button>
      </div>
      <div class="modal-body">
        <ul id="confirmDestructivaConsecuencias" class="confirm-destructiva-consecuencias"></ul>
        <p id="confirmDestructivaNota" class="confirm-destructiva-nota" style="display:none;"></p>
        <label id="confirmDestructivaOpcionWrap" class="confirm-destructiva-opcion" style="display:none;">
          <input type="checkbox" id="confirmDestructivaOpcionCheck" />
          <span id="confirmDestructivaOpcionTexto"></span>
        </label>
        <div id="confirmDestructivaSelectWrap" class="confirm-destructiva-select-wrap" style="display:none;">
          <label class="form-label" id="confirmDestructivaSelectLabel"></label>
          <select id="confirmDestructivaSelect" class="form-input form-select"></select>
        </div>
        <label id="confirmDestructivaTypeWrap" class="confirm-destructiva-label" style="display:none;">
          ${t('Escribí')} <strong id="confirmDestructivaTextoEsperado"></strong> ${t('para confirmar:')}
          <input
            type="text"
            id="confirmDestructivaInput"
            class="form-input confirm-destructiva-input"
            autocomplete="off"
            autocorrect="off"
            autocapitalize="off"
            spellcheck="false"
          />
        </label>
      </div>
      <div class="modal-footer">
        <button class="btn btn-secondary" data-action="confirmDestructiva:cancelar">${t('Cancelar')}</button>
        <button class="btn btn-danger" id="confirmDestructivaAccion" data-action="confirmDestructiva:aceptar" disabled>
          ${t('Confirmar')}
        </button>
      </div>
    </div>`;
    },

    /**
     * Abre el modal y devuelve una Promise<{confirmado:boolean, opcionMarcada:boolean}>.
     * @param {Object} opts
     * @param {string} opts.titulo
     * @param {string} [opts.textoConfirmacion] - si se pasa, exige teclearlo (destructivo).
     * @param {string[]} [opts.consecuencias]
     * @param {string} [opts.nota]
     * @param {string} [opts.etiquetaAccion='Confirmar']
     * @param {{texto:string, marcadaPorDefecto?:boolean}} [opts.opcion] - checkbox extra (ej. cascada).
     * @param {{label:string, opcionesHtml:string}} [opts.selector] - <select> extra (ej. nodo destino).
     *        opcionesHtml debe venir YA escapado (son <option> de datos saneados).
     * @param {'destructivo'|'neutral'} [opts.tono] - estilo visual. Default: destructivo si hay
     *        textoConfirmacion (rojo + ⚠), neutral si no (acción positiva: activar/reactivar).
     * @returns {Promise<{confirmado:boolean, opcionMarcada:boolean, valorSeleccionado:string|null}>}
     */
    pedir(opts) {
        const {
            titulo = t('Confirmar acción'),
            textoConfirmacion = '',
            consecuencias = [],
            nota = '',
            etiquetaAccion = t('Confirmar'),
            opcion = null,
            selector = null,
            tono = null,
        } = opts || {};

        // Si ya hay una confirmación abierta, la cancelamos antes de abrir otra
        // (no debería pasar — los flujos son secuenciales — pero es defensivo).
        if (resolverPromesaEnCurso) finalizar(false);

        // El gate type-to-confirm solo aplica si hay texto a teclear.
        const exigeTipeo = normalizarTextoConfirmacion(textoConfirmacion).length > 0;
        textoEsperadoNormalizado = exigeTipeo ? normalizarTextoConfirmacion(textoConfirmacion) : '';

        // Tono visual: explícito si se pasa, sino destructivo cuando hay tipeo.
        const esDestructivo = tono ? tono === 'destructivo' : exigeTipeo;

        const tituloEl = document.getElementById('confirmDestructivaTitulo');
        const headerEl = document.getElementById('confirmDestructivaHeader');
        const iconoEl = document.getElementById('confirmDestructivaIcono');
        const modalEl = document.getElementById(MODAL_ID);
        const consecuenciasEl = document.getElementById('confirmDestructivaConsecuencias');
        const notaEl = document.getElementById('confirmDestructivaNota');
        const textoEsperadoEl = document.getElementById('confirmDestructivaTextoEsperado');
        const typeWrapEl = document.getElementById('confirmDestructivaTypeWrap');
        const inputEl = document.getElementById('confirmDestructivaInput');
        const opcionWrapEl = document.getElementById('confirmDestructivaOpcionWrap');
        const opcionCheckEl = document.getElementById('confirmDestructivaOpcionCheck');
        const opcionTextoEl = document.getElementById('confirmDestructivaOpcionTexto');
        const botonAccionEl = document.getElementById('confirmDestructivaAccion');

        if (!tituloEl || !botonAccionEl) {
            // El modal no se inyectó: fail-safe a NO ejecutar la acción.
            console.error('[ConfirmacionDestructiva] modal no encontrado en el DOM');
            return Promise.resolve({ confirmado: false, opcionMarcada: false });
        }

        // Aplicar tono: destructivo = rojo + ícono ⚠ pulsante; neutral = sobrio.
        modalEl.classList.toggle('modal-destructivo', esDestructivo);
        headerEl.classList.toggle('modal-header-destructivo', esDestructivo);
        iconoEl.classList.toggle('icon-alerta-pulsante', esDestructivo);
        iconoEl.src = esDestructivo ? '/assets/icons/alert-triangle.svg' : '/assets/icons/check-circle.svg';
        botonAccionEl.classList.toggle('btn-danger', esDestructivo);
        botonAccionEl.classList.toggle('btn-primary', !esDestructivo);

        tituloEl.textContent = titulo;
        botonAccionEl.textContent = etiquetaAccion;

        // Consecuencias: lista escapada (vienen de datos de DB/usuario).
        consecuenciasEl.innerHTML = (consecuencias || [])
            .map(c => `<li>${escapeHtmlParaInyeccionSegura(c)}</li>`)
            .join('');

        if (nota) {
            notaEl.textContent = nota;
            notaEl.style.display = 'block';
        } else {
            notaEl.style.display = 'none';
        }

        // Checkbox opcional (ej. cascada). textContent escapa solo.
        if (opcion && opcion.texto) {
            opcionTextoEl.textContent = opcion.texto;
            opcionCheckEl.checked = !!opcion.marcadaPorDefecto;
            opcionWrapEl.style.display = 'flex';
        } else {
            opcionCheckEl.checked = false;
            opcionWrapEl.style.display = 'none';
        }

        // Selector extra (ej. nodo destino de reasignación). opcionesHtml ya viene
        // escapado por el caller (son <option> con texto de DB ya saneado).
        const selectWrapEl = document.getElementById('confirmDestructivaSelectWrap');
        const selectEl = document.getElementById('confirmDestructivaSelect');
        const selectLabelEl = document.getElementById('confirmDestructivaSelectLabel');
        if (selector && selector.opcionesHtml) {
            selectLabelEl.textContent = selector.label || '';
            selectEl.innerHTML = selector.opcionesHtml;
            selectWrapEl.style.display = 'block';
        } else {
            selectEl.innerHTML = '';
            selectWrapEl.style.display = 'none';
        }

        // Modo type-to-confirm vs confirmación simple.
        if (exigeTipeo) {
            textoEsperadoEl.textContent = textoConfirmacion;
            typeWrapEl.style.display = 'block';
            inputEl.value = '';
            botonAccionEl.disabled = true;

            // El botón se habilita SOLO cuando lo tecleado coincide exacto.
            inputEl.oninput = () => {
                botonAccionEl.disabled = normalizarTextoConfirmacion(inputEl.value) !== textoEsperadoNormalizado;
            };
            // Enter acepta SOLO si ya coincide (no salta el gate).
            inputEl.onkeydown = e => {
                if (e.key === 'Enter' && !botonAccionEl.disabled) {
                    e.preventDefault();
                    this.aceptar();
                }
            };
        } else {
            // Confirmación simple: sin input, botón habilitado de entrada.
            typeWrapEl.style.display = 'none';
            inputEl.oninput = null;
            inputEl.onkeydown = null;
            botonAccionEl.disabled = false;
        }

        // Escape cancela (captura, para ganarle a otros handlers globales).
        listenerTecladoActivo = e => {
            if (e.key === 'Escape') {
                e.preventDefault();
                this.cancelar();
            }
        };
        document.addEventListener('keydown', listenerTecladoActivo, true);

        const promesa = new Promise(resolve => {
            resolverPromesaEnCurso = resolve;
        });

        App.openModal(MODAL_ID);
        // Foco tras el render: al input si hay tipeo, sino al botón de acción.
        requestAnimationFrame(() => (exigeTipeo ? inputEl : botonAccionEl).focus());

        return promesa;
    },

    aceptar() {
        // Doble defensa: si exige tipeo, re-verificamos la coincidencia exacta
        // antes de confirmar (un click programático podría bypassear el disabled).
        if (textoEsperadoNormalizado) {
            const inputEl = document.getElementById('confirmDestructivaInput');
            const coincide = normalizarTextoConfirmacion(inputEl?.value) === textoEsperadoNormalizado;
            if (!coincide) return; // no confirmar si no coincide
        }
        finalizar(true);
    },

    cancelar() {
        finalizar(false);
    },
};
